/**
 * The colours the What's-New module wears in the DRA.
 *
 * The module asks for these rather than choosing them, and the reason is worth keeping in front
 * of whoever edits this file: **a Tailwind name does not mean the same thing in two apps.** In
 * `customer360-app` `blue-*` IS the Dolder green, because its config remaps the whole scale; in
 * `pure-app` there is no `info` scale at all, so `bg-info-50` rendered as no background
 * whatsoever. One hard-coded map produced two different wrong answers, so the host passes its
 * own.
 *
 * ## Why the DRA passes a full set although stock Tailwind still compiles here
 *
 * `supplier-order-app` had to rewrite 67 classes because it switches the stock palette OFF and
 * they compiled to nothing. The DRA does not: `tailwind.config.js` uses `extend`, so
 * `bg-green-50` would render. **It would render and still be wrong**, which is the harder case
 * to notice. `design.md` §8 assigns meanings — the Dolder green is "done", `--info` is "read by
 * the agent", amber is "a person has to act", red is an error — and stock `green-600` is not the
 * brand green (P38 removed the last of it, and `scan-tokens` counts what is left). A module
 * wearing approximately-right colours in the middle of a screen that is exact is worse than one
 * wearing obviously foreign ones: nobody goes looking.
 *
 * Every pair below is in `design.md` §2's measured contrast table. Nothing here re-picks a value.
 *
 * ## The three kinds, and why `fixed` is grey
 *
 * `new` takes the brand tint: something exists that did not. `improved` takes `--info`, the
 * app's "this is a reading, not a state" colour. `fixed` is NEUTRAL, and deliberately not amber —
 * §8 reserves amber for *a person has to act*, and a bug that is already fixed asks nothing of
 * anybody. That reasoning is the module's own and travels with it; only the class names here are
 * the DRA's business.
 *
 * ## `overlay` is not a colour
 *
 * The panel and the blocking dialog need something to lift them off the page. §2 has a token for
 * it (`--overlay-shadow`, exposed as `shadow-overlay`); a stock Tailwind app has `shadow-xl`,
 * which is what the module defaults to and what P38 removed from this app.
 */

import type { DeepPartialTones } from '../whats-new/tones';

export const DRA_TONES: DeepPartialTones = {
    kind: {
        // --brand-700 on --brand-50 = 5.83:1
        new: 'bg-brand-50 text-brand-700 border-brand-100',
        // --info on --info-50 = 6.03:1
        improved: 'bg-info-50 text-info border-info-100',
        // --muted on --head = 5.44:1
        fixed: 'bg-head text-muted border-hair',
    },
    notice: {
        info: {
            bar: 'border-info-100 bg-info-50 text-info',
            icon: 'text-info',
            button: 'text-info hover:bg-info-100',
        },
        warning: {
            // --warn-ink on --warn-50 = 7.71:1. The edge is --warn-edge, which is what §8 calls
            // the amber border; the icon is --warn, which §2 marks "icon only".
            bar: 'border-warn-edge bg-warn-50 text-warn-ink',
            icon: 'text-warn',
            button: 'text-warn-ink hover:bg-warn-edge/30',
        },
        critical: {
            // --err on --err-50 = 6.78:1. Never a filled red surface (§2) — the tint and the
            // edge carry it, exactly as the kit's destructive button does.
            bar: 'border-err-edge bg-err-50 text-err',
            icon: 'text-err',
            button: 'text-err hover:bg-err-edge/30',
        },
    },
    overlay: 'shadow-overlay',
};
